import { useEffect, useRef, useCallback } from "react";
import websocketService from "../services/websocketService";
import * as localService from "../services/localService";
import logger from "../utils/logger";

/**
 * Custom hook for WebSocket booking updates
 * @param {Function} onBookingUpdate - Callback when booking status changes
 * @returns {boolean} wsConnected - WebSocket connection status
 */
const useBookingWebSocket = onBookingUpdate => {
  // Store subscription references for cleanup
  const subscriptionsRef = useRef([]);
  // Store the latest callback in a ref to avoid recreating subscriptions
  const callbackRef = useRef(onBookingUpdate);

  // Update the callback ref when it changes
  useEffect(() => {
    callbackRef.current = onBookingUpdate;
  }, [onBookingUpdate]);

  // Stable callback that reads from ref
  const handleBookingUpdate = useCallback(update => {
    logger.log("Booking update received:", update);
    if (callbackRef.current) {
      callbackRef.current(update);
    }
  }, []);

  useEffect(() => {
    const currentUser = localService.getUser();
    if (!currentUser) return;

    const wsUrl = process.env.REACT_APP_WS_URL || process.env.REACT_APP_BASE_URL || "https://api.spacia.africa/";

    websocketService
      .connect(wsUrl)
      .then(() => {
        logger.log("WebSocket connected for bookings");

        // Subscribe and store subscription references for cleanup
        if (currentUser.organizationId) {
          const orgSub = websocketService.subscribeToOrganizationBookings(
            currentUser.organizationId,
            handleBookingUpdate
          );
          if (orgSub) {
            subscriptionsRef.current.push(orgSub);
          }
        }

        const userSub = websocketService.subscribeToUserQueue(handleBookingUpdate);
        if (userSub) {
          subscriptionsRef.current.push(userSub);
        }
      })
      .catch(_err => {
        logger.log("⚠️ WebSocket not available - using polling mode");
      });

    // Cleanup: unsubscribe this component's callbacks on unmount
    // This removes the component's callbacks but keeps the connection alive
    return () => {
      subscriptionsRef.current.forEach(sub => {
        if (sub && sub.unsubscribe) {
          sub.unsubscribe();
        }
      });
      subscriptionsRef.current = [];
      logger.log("Cleaned up booking WebSocket subscriptions");
    };
  }, [handleBookingUpdate]);

  return websocketService.isConnected();
};

export default useBookingWebSocket;
